import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FileDropDialogComponent } from './file-drop-dialog.component';

export interface FileDropOptions {
    /** dialog heading, usually the label of the button that opened it */
    title?: string;
    /** the `accept` attribute the native input used to have */
    accept?: string;
    /** allowed extensions; derived from `accept` when omitted */
    fileTypes?: string[];
    maxFileSizeMb?: number;
    /** false for uploads that only take one file */
    multiple?: boolean;
}

const MIME_GROUPS = {
    'image/*': ['png', 'jpg', 'jpeg', 'jp2', 'tif', 'tiff', 'bmp', 'gif', 'webp', 'svg'],
    'video/*': ['mp4', 'mov', 'm4v', 'avi', 'wmv', 'mpg', 'mpeg', 'mkv', 'webm'],
    'audio/*': ['mp3', 'wav', 'm4a', 'aac', 'ogg']
};

const MIME_EXTENSIONS = {
    'application/pdf': ['pdf'],
    'image/jpeg': ['jpeg', 'jpg'],
    'image/jpg': ['jpg'],
    'image/jp2': ['jp2'],
    'image/png': ['png'],
    'image/bmp': ['bmp'],
    'image/svg+xml': ['svg'],
    'image/tiff': ['tif', 'tiff'],
    'image/jfif': ['jfif'],
    'image/gif': ['gif'],
    'image/webp': ['webp'],
    'application/json': ['json'],
    'application/zip': ['zip'],
    'application/msword': ['doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
    'application/vnd.ms-excel': ['xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
    'text/csv': ['csv'],
    'text/plain': ['txt']
};

/** Everything we let through when a site never restricted the file type. */
const ANY_FILE_TYPES = ['pdf', 'png', 'jpg', 'jpeg', 'jp2', 'tif', 'tiff', 'bmp', 'gif', 'svg',
    'doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx', 'txt', 'zip', 'mp4', 'mov'];

/**
 * Opens the shared drag & drop dialog in place of a hidden `<input type="file">`.
 * Emits once, with the files the user confirmed; never emits when the dialog is
 * cancelled, so callers can keep their upload logic in a plain subscribe.
 */
@Injectable({providedIn: 'root'})
export class FileDropService {
    static maxFileSizeMb = 16;

    constructor(private dialog: MatDialog) {
    }

    pick(options: FileDropOptions = {}): Observable<File[]> {
        const dialogRef = this.dialog.open(FileDropDialogComponent, {
            width: '600px',
            data: {
                title: options.title || 'Upload file',
                accept: options.accept || '',
                fileTypes: options.fileTypes || FileDropService.typesFromAccept(options.accept),
                maxFileSizeMb: options.maxFileSizeMb || FileDropService.maxFileSizeMb,
                multiple: options.multiple !== false
            }
        });
        return dialogRef.afterClosed().pipe(
            filter((files: File[]) => !!files && files.length > 0)
        );
    }

    /** `".csv"` -> `['csv']`, `"image/*"` -> every image extension, empty -> everything. */
    static typesFromAccept(accept: string | null | undefined): string[] {
        if (!accept || accept.trim() === '') {
            return ANY_FILE_TYPES.slice();
        }
        const types: string[] = [];
        accept.split(',').forEach(rawEntry => {
            const entry = rawEntry.trim().toLowerCase();
            if (entry === '') {
                return;
            }
            let extensions: string[];
            if (MIME_GROUPS[entry]) {
                extensions = MIME_GROUPS[entry];
            } else if (MIME_EXTENSIONS[entry]) {
                extensions = MIME_EXTENSIONS[entry];
            } else if (entry.indexOf('/') !== -1) {
                // a mime type we have no extension for - better to allow it than to
                // reject a file the old input would have taken
                return;
            } else {
                // ".csv" or, for the odd malformed accept in older templates, "csv"
                extensions = [entry.replace(/^\./, '')];
            }
            extensions.forEach(ext => {
                if (types.indexOf(ext) === -1) {
                    types.push(ext);
                }
            });
        });
        return types.length > 0 ? types : ANY_FILE_TYPES.slice();
    }
}
